import { CheckCircle, CircleNotch, Lock, XCircle } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@reactive-resume/ui";
import { useEffect, useState } from "react";

import { usePayment } from "@/client/hooks/use-payment";
import { useSubscription } from "@/client/hooks/use-subscription";

type PaymentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type PlanCardProps = {
  type: "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION";
  price: number;
  features: string[];
  popular?: boolean;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
};

const PlanCard = ({ type, price, features, popular, badge, selected, onSelect }: PlanCardProps) => {
  return (
    <button
      type="button"
      className={`relative w-full rounded-lg border-2 p-6 text-left transition-all ${
        selected
          ? "border-accent bg-accent/5"
          : "border-border hover:border-accent/50 hover:bg-secondary/50"
      }`}
      onClick={onSelect}
    >
      {popular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground">
          Popular
        </Badge>
      )}
      {badge && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-success text-success-foreground">
          {badge}
        </Badge>
      )}
      <div className="mb-4">
        <h3 className="text-lg font-semibold">
          {type === "ONE_TIME_DOWNLOAD" ? "One-Time Download" : "Monthly Subscription"}
        </h3>
        <div className="mt-2">
          <span className="text-3xl font-bold">KES {price}</span>
        </div>
      </div>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <CheckCircle className="mt-0.5 size-4 shrink-0 text-success" weight="fill" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {selected && (
        <div className="mt-4 flex items-center justify-center gap-2 text-accent">
          <CheckCircle className="size-5" weight="fill" />
          <span className="text-sm font-medium">Selected</span>
        </div>
      )}
    </button>
  );
};

export const PaymentModal = ({ isOpen, onClose, onSuccess }: PaymentModalProps) => {
  const { state, initiatePayment, reset, getPlanDetails } = usePayment();
  const { refresh } = useSubscription();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<
    "ONE_TIME_DOWNLOAD" | "MONTHLY_SUBSCRIPTION" | null
  >(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setEmail("");
      setEmailError("");
      setSelectedPlan(null);
    }
  }, [isOpen, reset]);

  // Auto-close and refresh on success
  useEffect(() => {
    if (state.step === "success") {
      void refresh();
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 3000);
    }
  }, [state.step, onSuccess, onClose, refresh]);

  const validateEmail = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setEmailError("Email is required");
      return false;
    }
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!ok) {
      setEmailError("Enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value) {
      validateEmail(value);
    } else {
      setEmailError("");
    }
  };

  const handlePay = () => {
    if (!selectedPlan) {
      return;
    }

    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      return;
    }

    void initiatePayment(selectedPlan, email);
  };

  const renderContent = () => {
    switch (state.step) {
      case "idle":
      case "selecting": {
        if (state.plansLoading) {
          return (
            <>
              <DialogHeader>
                <DialogTitle>Choose Your Plan</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-center">
                <CircleNotch className="mx-auto size-12 animate-spin text-accent" />
                <p className="text-sm">Loading payment plans...</p>
              </div>
            </>
          );
        }

        if (state.availablePlans.length === 0) {
          return (
            <>
              <DialogHeader>
                <DialogTitle>Choose Your Plan</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-center">
                <XCircle className="mx-auto size-12 text-error" weight="fill" />
                <p className="text-sm">
                  {state.error ?? "Payment plans are not available right now. Please try again."}
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    reset();
                  }}
                >
                  Retry
                </Button>
              </div>
            </>
          );
        }

        const oneTime = getPlanDetails("ONE_TIME_DOWNLOAD");
        const monthly = getPlanDetails("MONTHLY_SUBSCRIPTION");

        return (
          <>
            <DialogHeader>
              <DialogTitle>Choose Your Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {oneTime && (
                  <PlanCard
                    popular
                    type="ONE_TIME_DOWNLOAD"
                    price={oneTime.price}
                    features={["Download once", "All templates", "PDF format"]}
                    selected={selectedPlan === "ONE_TIME_DOWNLOAD"}
                    onSelect={() => {
                      setSelectedPlan("ONE_TIME_DOWNLOAD");
                    }}
                  />
                )}
                {monthly && (
                  <PlanCard
                    type="MONTHLY_SUBSCRIPTION"
                    price={monthly.price}
                    features={[
                      "Unlimited downloads",
                      "All templates",
                      "Valid for 30 days",
                      "Priority support",
                    ]}
                    badge="Best Value"
                    selected={selectedPlan === "MONTHLY_SUBSCRIPTION"}
                    onSelect={() => {
                      setSelectedPlan("MONTHLY_SUBSCRIPTION");
                    }}
                  />
                )}
              </div>

              {selectedPlan && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      className={emailError ? "border-error" : ""}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? "email-error" : undefined}
                      onChange={(e) => {
                        handleEmailChange(e.target.value);
                      }}
                    />
                    {emailError && (
                      <p id="email-error" className="text-sm text-error" role="alert">
                        {emailError}
                      </p>
                    )}
                  </div>

                  <Button
                    className="w-full bg-accent text-accent-foreground hover:bg-accent-accent"
                    size="lg"
                    onClick={handlePay}
                  >
                    Continue to Payment
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-medium-gray">
                <Lock className="size-4" />
                <span>Pay via M-Pesa, Visa, Mastercard, Airtel Money, or Bank Transfer.</span>
              </div>
            </div>
          </>
        );
      }

      case "processing": {
        return (
          <>
            <DialogHeader>
              <DialogTitle>Preparing Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-center">
              <CircleNotch className="mx-auto size-12 animate-spin text-accent" />
              <p className="text-sm">Preparing your Pesapal checkout...</p>
              <p className="text-xs text-medium-gray">Please wait a moment</p>
            </div>
          </>
        );
      }

      case "waiting": {
        return (
          <>
            <DialogHeader>
              <DialogTitle>Redirecting to Checkout</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-center">
              <CircleNotch className="mx-auto size-12 animate-spin text-accent" />
              <p className="text-sm">You will be redirected to Pesapal to complete payment</p>
              <Button variant="outline" className="w-full" onClick={onClose}>
                Cancel Payment
              </Button>
            </div>
          </>
        );
      }

      case "success": {
        return (
          <>
            <DialogHeader>
              <DialogTitle>Payment Successful!</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-center">
              <CheckCircle className="mx-auto size-16 text-success" weight="fill" />
              <p className="text-sm font-medium">Your payment was successful</p>
              <p className="text-xs text-medium-gray">Redirecting...</p>
            </div>
          </>
        );
      }

      case "failed": {
        return (
          <>
            <DialogHeader>
              <DialogTitle>Payment Failed</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-center">
              <XCircle className="mx-auto size-16 text-error" weight="fill" />
              <p className="text-sm">{state.error ?? "Payment failed. Please try again."}</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={onClose}>
                  Close
                </Button>
                <Button
                  className="flex-1 bg-accent text-accent-foreground"
                  onClick={() => {
                    reset();
                    setSelectedPlan(null);
                    setEmail("");
                    setEmailError("");
                  }}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">{renderContent()}</DialogContent>
    </Dialog>
  );
};
